import { DynamicWagmiConnector } from '@dynamic-labs/wagmi-connector';
import { type ReactNode } from 'react';

import { AblyProvider } from './ably';
import { DecentProvider } from './decent';
import { DynamicProvider } from './dynamic';
import { ReactQueryProvider } from './react-query';
// import { ReservoirProvider } from './reservoir';
// import { SequenceProvider } from './sequence';
import { WagmiProviderCore } from './wagmi';

interface ProvidersProps {
  enableAbly?: boolean;
  enableDecent?: boolean;
  enableMarketplace?: boolean;
  enableSequence?: boolean;
  children: React.ReactNode;
  chains?: ('evm' | 'sol')[];
  dynamicEnvironmentId?: string;
}

/**
 * Providers component that wraps its children with various context providers.
 * @component
 * @param {ProvidersProps} props - The properties for the component.
 * @param {boolean} [props.enableAbly=false] - Whether to enable Ably.
 * @param {boolean} [props.enableDecent=false] - Whether to enable Decent.
 * @param {boolean} [props.enableMarketplace=false] - Whether to enable Marketplace.
 * @param {boolean} [props.enableSequence=false] - Whether to enable Sequence.
 * @param {React.ReactNode} props.children - The child components to be wrapped by the providers.
 * @param {('evm' | 'sol')[]} [props.chains] - The chains to be used.
 * @returns {JSX.Element} The Providers component.
 */
export const Providers: React.FC<ProvidersProps> = ({
  enableAbly = false,
  enableDecent = false,
  enableMarketplace = false,
  enableSequence = false,
  children,
  chains,
  dynamicEnvironmentId,
}: ProvidersProps) => {
  const withOptionalProviders = (content: ReactNode) => {
    if (enableAbly) {
      content = <AblyProvider>{content}</AblyProvider>;
    }
    if (enableDecent) {
      content = <DecentProvider>{content}</DecentProvider>;
    }
    // if (enableMarketplace) {
    //   content = <ReservoirProvider>{content}</ReservoirProvider>;
    // }
    // if (enableSequence) {
    //   content = <SequenceProvider>{content}</SequenceProvider>;
    // }
    return content;
  };

  return (
    <DynamicProvider
      dynamicEnvironmentId={dynamicEnvironmentId}
      chains={chains}
    >
      <WagmiProviderCore>
        <ReactQueryProvider>
          {withOptionalProviders(
            <DynamicWagmiConnector>{children}</DynamicWagmiConnector>,
          )}
        </ReactQueryProvider>
      </WagmiProviderCore>
    </DynamicProvider>
  );
};
